import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { faPalette } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useFocusWithin } from "@vueuse/core";
import { BButton, BButtonGroup } from "bootstrap-vue";
import { sanitize } from "dompurify";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useWorkflowStores } from "@/composables/workflowStores";
import { colors } from "./colors";
import { useResizable } from "./useResizable";
import { selectAllText } from "./utilities";
import ColorSelector from "./ColorSelector.vue";
import DraggablePan from "@/components/Workflow/Editor/DraggablePan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'TextComment',
    props: {
        comment: null,
        rootOffset: null,
        scale: null,
        readonly: { type: Boolean }
    },
    emits: ["change", "resize", "move", "pan-by", "remove", "set-color"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faTrashAlt, faPalette);
        const resizeContainer = ref();
        useResizable(resizeContainer, computed(() => props.comment.size), ([width, height]) => {
            emit("resize", [width, height]);
            saveText();
        });
        function escapeAndSanitize(text) {
            return sanitize(text, { ALLOWED_TAGS: ["br"] }).replace(/(?:^(\s|&nbsp;)+)|(?:(\s|&nbsp;)+$)/g, "");
        }
        const editableElement = ref();
        function getInnerText() {
            const element = editableElement.value;
            if (element) {
                const value = element.innerHTML ?? "";
                return escapeAndSanitize(value);
            }
            else {
                return "";
            }
        }
        function saveText() {
            const text = getInnerText();
            if (text !== props.comment.data.text) {
                emit("change", { ...props.comment.data, text });
            }
        }
        function toggleBold() {
            if (props.comment.data.bold) {
                const { bold: _bold, ...data } = props.comment.data;
                emit("change", data);
            }
            else {
                emit("change", { ...props.comment.data, bold: true });
            }
        }
        function toggleItalic() {
            if (props.comment.data.italic) {
                const { italic: _italic, ...data } = props.comment.data;
                emit("change", data);
            }
            else {
                emit("change", { ...props.comment.data, italic: true });
            }
        }
        const fontSize = computed(() => props.comment.data.size);
        const canIncreaseFontSize = computed(() => fontSize.value < 5);
        const canDecreaseFontSize = computed(() => fontSize.value > 1);
        function increaseFontSize() {
            if (canIncreaseFontSize.value) {
                emit("change", { ...props.comment.data, size: fontSize.value + 1 });
            }
        }
        const increaseFontSizeTitle = computed(() => canIncreaseFontSize.value ? `Increase font size to ${fontSize.value + 1}` : "Maximum font size");
        function decreaseFontSize() {
            if (canDecreaseFontSize.value) {
                emit("change", { ...props.comment.data, size: fontSize.value - 1 });
            }
        }
        const decreaseFontSizeTitle = computed(() => canDecreaseFontSize.value ? `Decrease font size to ${fontSize.value - 1}` : "Minimum font size");
        function onRootClick() {
            if (!props.readonly) {
                editableElement.value?.focus();
            }
        }
        function onMove(position) {
            emit("move", [position.x, position.y]);
        }
        const showColorSelector = ref(false);
        const rootElement = ref();
        const { focused } = useFocusWithin(rootElement);
        watch(() => focused.value, () => {
            if (!focused.value) {
                showColorSelector.value = false;
                if (getInnerText() === "") {
                    emit("remove");
                }
                else {
                    saveText();
                }
            }
        });
        function onSetColor(color) {
            emit("set-color", color);
        }
        const cssVariables = computed(() => {
            const vars = {
                "--font-size": `${fontSize.value}rem`,
            };
            if (props.comment.color !== "none") {
                vars["--font-color"] = colors[props.comment.color];
            }
            return vars;
        });
        const { commentStore } = useWorkflowStores();
        function onDoubleClick() {
            if (editableElement.value) {
                selectAllText(editableElement.value);
            }
        }
        onMounted(() => {
            if (commentStore.isJustCreated(props.comment.id) && editableElement.value) {
                selectAllText(editableElement.value);
            }
        });
        const position = computed(() => ({ x: props.comment.position[0], y: props.comment.position[1] }));
        return { __sfc: true, props, emit, resizeContainer, escapeAndSanitize, editableElement, getInnerText, saveText, toggleBold, toggleItalic, fontSize, canIncreaseFontSize, canDecreaseFontSize, increaseFontSize, increaseFontSizeTitle, decreaseFontSize, decreaseFontSizeTitle, onRootClick, onMove, showColorSelector, rootElement, focused, onSetColor, cssVariables, commentStore, onDoubleClick, position, FontAwesomeIcon, BButton, BButtonGroup, reactive, ColorSelector, DraggablePan };
    }
});
